import React, { useEffect, useId, useRef, useState } from 'react';
import { FiCheck, FiChevronDown, FiChevronsDown, FiChevronsUp, FiClock, FiCopy, FiExternalLink, FiFileText, FiVideo } from 'react-icons/fi';
import './WorkspaceMeetingTimeline.css';

const monthLabel = new Intl.DateTimeFormat('en', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const dayLabel = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', timeZone: 'UTC' });
const meetingDate = value => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value ? date : null;
};
const safeUrl = value => {
  try { const url = new URL(value); return url.protocol === 'https:' && !url.username && !url.password ? url.href : null; }
  catch { return null; }
};
const highlight = (text, query) => {
  const term = query.trim();
  const index = term ? text.toLowerCase().indexOf(term.toLowerCase()) : -1;
  return index < 0 ? text : <>{text.slice(0, index)}<mark>{text.slice(index, index + term.length)}</mark>{text.slice(index + term.length)}</>;
};

export default function WorkspaceMeetingTimeline({ meetings, query = '', focusTarget }) {
  const [expanded, setExpanded] = useState({});
  const [openMonths, setOpenMonths] = useState({});
  const [copyStatus, setCopyStatus] = useState(null);
  const entries = useRef(new Map());
  const prefix = useId();
  const targetVisible = meetings.some(meeting => meeting.id === focusTarget?.assetId);
  useEffect(() => { setExpanded({}); setOpenMonths({}); }, [query]);
  useEffect(() => {
    const entry = entries.current.get(focusTarget?.assetId);
    if (!entry) return undefined;
    setExpanded(current => ({ ...current, [focusTarget.assetId]: true }));
    setOpenMonths(current => ({ ...current, [entry.dataset.month]: true }));
    const frame = requestAnimationFrame(() => {
      entry.focus({ preventScroll: true });
      entry.scrollIntoView({ block: 'center', behavior: 'auto' });
    });
    return () => cancelAnimationFrame(frame);
  }, [focusTarget, targetVisible]);

  const isOpen = meeting => Boolean(expanded[meeting.id]);
  const months = new Map();
  [...meetings].sort((a, b) => (meetingDate(b.recordedAt)?.getTime() || 0) - (meetingDate(a.recordedAt)?.getTime() || 0)).forEach(meeting => {
    const date = meetingDate(meeting.recordedAt);
    const key = date ? meeting.recordedAt.slice(0, 7) : 'undated';
    if (!months.has(key)) months.set(key, { label: date ? monthLabel.format(date) : 'Undated meetings', entries: [] });
    months.get(key).entries.push(meeting);
  });
  const latestMonth = months.keys().next().value;
  const isMonthOpen = key => openMonths[key] ?? (Boolean(query.trim()) || key === latestMonth);
  const allOpen = [...months.keys()].every(isMonthOpen) && meetings.every(isOpen);
  const allClosed = [...months.keys()].every(key => !isMonthOpen(key));
  const toggleAll = open => {
    setOpenMonths(Object.fromEntries([...months.keys()].map(key => [key, open])));
    setExpanded(Object.fromEntries(meetings.map(meeting => [meeting.id, open])));
  };
  const recorded = meetings.filter(meeting => meeting.recording).length;
  const copy = async meeting => {
    try {
      await navigator.clipboard.writeText(meeting.recording.passcode);
      setCopyStatus({ id: meeting.id, ok: true });
    } catch { setCopyStatus({ id: meeting.id, ok: false }); }
  };

  return <section className="workspace-meetings" aria-label="Meeting timeline">
    <header className="workspace-meetings__header">
      <div><h2>Meetings <span>{meetings.length}</span></h2><p>{recorded} recordings <span aria-hidden="true">/</span> {meetings.length - recorded} transcript-only</p></div>
      <div className="workspace-meetings__controls">
        <button type="button" className="deal-desk__button" disabled={allOpen} onClick={() => toggleAll(true)}><FiChevronsDown aria-hidden="true" />Expand all</button>
        <button type="button" className="deal-desk__button" disabled={allClosed} onClick={() => toggleAll(false)}><FiChevronsUp aria-hidden="true" />Collapse all</button>
      </div>
    </header>
    {[...months.entries()].map(([key, month]) => <section className="workspace-meetings__month" key={key} aria-labelledby={`${prefix}-${key}`}>
      <h3 id={`${prefix}-${key}`}><button type="button" className="workspace-meetings__month-toggle" aria-expanded={isMonthOpen(key)} aria-controls={`${prefix}-${key}-meetings`}
        onClick={() => setOpenMonths(current => ({ ...current, [key]: !isMonthOpen(key) }))}>
        <FiChevronDown className="workspace-meetings__chevron" aria-hidden="true" /><span>{month.label}</span><span className="workspace-meetings__count">{month.entries.length}</span>
      </button></h3>
      {/* Explicit role preserves list semantics in Safari when list-style is none. */}
      {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
      <ol id={`${prefix}-${key}-meetings`} className="workspace-meetings__timeline" role="list" hidden={!isMonthOpen(key)}>
        {month.entries.map(meeting => {
          const date = meetingDate(meeting.recordedAt), open = isOpen(meeting);
          const summary = Array.isArray(meeting.summary) ? meeting.summary.filter(point => typeof point === 'string' && point.trim()) : [];
          const recordingUrl = safeUrl(meeting.recording?.url), recordUrl = safeUrl(meeting.recordUrl);
          return <li key={meeting.id}>
            <article data-meeting-id={meeting.id}>
              <h4 aria-labelledby={`${prefix}-${meeting.id}-title`}>
                <button type="button" className="workspace-meetings__meeting-toggle" aria-expanded={open} aria-controls={`${prefix}-${meeting.id}-details`}
                  aria-labelledby={`${prefix}-${meeting.id}-title`} aria-describedby={`${prefix}-${meeting.id}-date ${prefix}-${meeting.id}-meta`} data-month={key}
                  ref={node => { if (node) entries.current.set(meeting.id, node); else entries.current.delete(meeting.id); }}
                  onClick={() => setExpanded(current => ({ ...current, [meeting.id]: !open }))}>
                  <span id={`${prefix}-${meeting.id}-date`} className="workspace-meetings__date">{date ? <time dateTime={meeting.recordedAt}>{dayLabel.format(date)}</time> : <span>Undated</span>}</span>
                  <span id={`${prefix}-${meeting.id}-title`} className="workspace-meetings__title">{highlight(meeting.title, query)}</span>
                  <span id={`${prefix}-${meeting.id}-meta`} className="workspace-meetings__meta"><span className={meeting.recording ? 'is-recorded' : 'is-transcript'}>{meeting.recording ? <FiVideo aria-hidden="true" /> : <FiFileText aria-hidden="true" />}{meeting.recording ? 'Recorded' : 'Transcript only'}</span>
                    {meeting.duration && <span><FiClock aria-hidden="true" />{meeting.duration}</span>}</span>
                  <FiChevronDown className="workspace-meetings__chevron" aria-hidden="true" />
                </button>
              </h4>
              <div id={`${prefix}-${meeting.id}-details`} className="workspace-meetings__details" hidden={!open}>
                <div className="workspace-meetings__summary">
                  {summary.length > 0 && <><h5>Summary</h5><ul>{summary.map((point, index) => <li key={index}>{highlight(point, query)}</li>)}</ul></>}
                  {meeting.nextSteps && <div className="workspace-meetings__next"><h5>Next steps</h5><p>{highlight(meeting.nextSteps, query)}</p></div>}
                </div>
                <div className="workspace-meetings__actions">
                  {recordingUrl && <a className="deal-desk__button" href={recordingUrl} target="_blank" rel="noreferrer"><FiExternalLink aria-hidden="true" />Watch recording</a>}
                  {meeting.recording && !recordingUrl && <span>Recording link unavailable</span>}
                  {recordUrl && <a className="deal-desk__button" href={recordUrl} target="_blank" rel="noreferrer"><FiFileText aria-hidden="true" />Open record</a>}
                  {recordingUrl && meeting.recording.passcode && <span className="workspace-meetings__passcode">Passcode <code>{meeting.recording.passcode}</code>
                    <button type="button" className="deal-desk__icon" aria-label={`Copy passcode for ${meeting.title}, ${meeting.recordedAt}`} title="Copy passcode" onClick={() => copy(meeting)}>
                      {copyStatus?.id === meeting.id && copyStatus.ok ? <FiCheck aria-hidden="true" /> : <FiCopy aria-hidden="true" />}</button></span>}
                  {copyStatus?.id === meeting.id && <span role="status" className={copyStatus.ok ? 'workspace-meetings__copied' : 'workspace-meetings__copy-error'}>{copyStatus.ok ? 'Copied' : 'Could not copy. Select the passcode instead.'}</span>}
                </div>
              </div>
            </article>
          </li>;
        })}
      </ol>
    </section>)}
  </section>;
}
